import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { MapPin, Clock, Phone, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export const LocationSection = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const storeInfo = [
    {
      icon: MapPin,
      title: t("location.address"),
      details: ["Marina of Port Ghalib", "Red Sea, Egypt"],
    },
    {
      icon: Clock,
      title: t("location.hours"),
      details: [t("location.openEveryDay"), t("location.hoursTime")],
    },
    {
      icon: MessageCircle,
      title: t("location.whatsapp"),
      details: ["+20 103 725 5878"],
    },
    {
      icon: Phone,
      title: t("location.contact"),
      details: ["lockeroom.egypt@gmail.com", "@locker_room_egypt"],
    },
  ];

  return (
    <section 
      id="location" 
      className="py-24 md:py-32 bg-black relative"
      ref={ref}
      data-testid="location-section"
    >
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#009A49] mb-6 font-body">
            {t("location.label")}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-wide">
            {t("location.heading")}
          </h2>
          <div className="emerald-divider mt-8" />
        </motion.div>
        
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Store Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-zinc-400 font-body text-lg leading-relaxed mb-10">
              {t("location.intro")}
            </p>
            
            <div className="space-y-8">
              {storeInfo.map((info, index) => (
                <div 
                  key={info.title}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border border-[#009A49]/30 flex items-center justify-center">
                    <info.icon className="w-5 h-5 text-[#009A49]" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-white mb-2">
                      {info.title}
                    </h4>
                    {info.details.map((detail, i) => {
                      if (info.title === "WhatsApp") {
                        return (
                          <a
                            key={i}
                            href="https://wa.me/201037255878"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-zinc-400 hover:text-[#009A49] font-body text-sm transition-colors"
                            data-testid="whatsapp-link"
                          >
                            {detail}
                          </a>
                        );
                      }
                      return (
                        <p key={i} className="text-zinc-500 font-body text-sm">
                          {detail}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Store interior image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-12 relative overflow-hidden rounded-sm"
            >
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/ofxq3t9s_Locker%20Notte.webp"
                alt={t("alts.storeImg")}
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 text-white font-body text-sm">
                {t("location.storeCaption")}
              </p>
            </motion.div>
          </motion.div>
          
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative h-full min-h-[500px] rounded-sm overflow-hidden border border-white/5">
              {/* Real Google Maps embed - styled dark */}
              <iframe
                title="Locker Room Store Location"
                src="https://maps.google.com/maps?q=25.534569,34.6375349&z=17&output=embed"
                className="w-full h-full min-h-[500px] map-dark"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map-embed"
              />
              
              {/* Info card overlay */}
              <div className="absolute bottom-4 left-4 right-4 glass px-6 py-4 rounded-sm pointer-events-none">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#009A49] flex-shrink-0" />
                  <div>
                    <p className="font-heading text-white text-lg font-semibold tracking-wide">Locker Room</p>
                    <p className="text-zinc-400 font-body text-sm">Marina of Port Ghalib · Red Sea, Egypt</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Directions link */}
            <a
              href="https://www.google.com/maps/place/Locker+Room/@25.534569,34.6375349,17z/data=!3m1!4b1!4m6!3m5!1s0x15b4a9a3935653ab:0x4df3f7f1b1055958!8m2!3d25.534569!4d34.6375349!16s%2Fg%2F11nbzxr0zy"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-[#009A49] hover:text-white font-body text-sm transition-colors"
              data-testid="get-directions-link"
            >
              <MapPin className="w-4 h-4" />
              {t("location.directions")}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
